import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Typography,
} from "@material-ui/core";
import { fade, makeStyles } from "@material-ui/core/styles";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";

import toastError from "../../errors/toastError";
import crmService from "../../services/crm";

const useStyles = makeStyles((theme) => ({
  root: {
    minWidth: 0,
  },
  header: {
    marginBottom: theme.spacing(1.5),
  },
  title: {
    fontWeight: 600,
  },
  description: {
    marginTop: theme.spacing(0.5),
  },
  list: {
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  item: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    gap: theme.spacing(1.5),
    alignItems: "center",
    padding: theme.spacing(1.5, 0.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  itemContent: {
    minWidth: 0,
  },
  ticketMeta: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(0.5),
  },
  message: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  state: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 120,
    padding: theme.spacing(2),
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: 8,
    color: theme.palette.text.secondary,
    textAlign: "center",
  },
  error: {
    borderColor: fade(theme.palette.error.main, 0.4),
    color: theme.palette.error.main,
    backgroundColor: fade(theme.palette.error.main, 0.035),
  },
}));

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("pt-BR");
};

const ACTIVE_CONVERSATION_STATUSES = ["open", "pending", "group"];

const ticketLinkErrorMessages = {
  ERR_CRM_CONTACT_NOT_FOUND:
    "O contato do negocio ou da conversa nao pertence a esta empresa.",
  ERR_CRM_DEAL_TICKET_CONTACT_REQUIRED:
    "Vincule um contato ao negocio para iniciar uma conversa.",
  ERR_CRM_TICKET_PERMISSION_DENIED:
    "Voce nao possui acesso a uma das conversas deste negocio.",
};

const DealTicketsPanel = ({
  dealId,
  contactName,
  onActiveConversationChange,
  onCountChange,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadLinks = useCallback(async () => {
    if (!dealId) return;
    setLoading(true);
    setError("");
    try {
      const result = await crmService.listDealTickets(dealId);
      const nextLinks = result?.links || [];
      const activeLink = nextLinks.find((link) =>
        ACTIVE_CONVERSATION_STATUSES.includes(link.ticket?.status),
      );
      setLinks(nextLinks);
      onActiveConversationChange?.({
        dealId: Number(dealId),
        ticket: activeLink?.ticket || null,
      });
    } catch (requestError) {
      const code =
        requestError?.response?.data?.error || requestError?.message;
      const message =
        ticketLinkErrorMessages[code] ||
        "Nao foi possivel carregar as conversas deste negocio.";
      setError(message);
      if (ticketLinkErrorMessages[code]) toast.error(message);
      else toastError(requestError);
      setLinks([]);
      onActiveConversationChange?.({
        dealId: Number(dealId),
        ticket: null,
      });
    } finally {
      setLoading(false);
    }
  }, [dealId, onActiveConversationChange]);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  useEffect(() => {
    onCountChange?.(links.length);
  }, [links.length, onCountChange]);

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography variant="h6" className={classes.title}>
          Conversas
        </Typography>
        <Typography
          variant="body2"
          color="textSecondary"
          className={classes.description}
        >
          Historico de conversas de {contactName || "este contato"}.
        </Typography>
      </Box>

      {loading ? (
        <Box className={classes.state} role="status">
          <CircularProgress size={24} aria-label="Carregando conversas" />
        </Box>
      ) : null}

      {!loading && error ? (
        <Box className={`${classes.state} ${classes.error}`} role="alert">
          <Typography variant="body2">{error}</Typography>
        </Box>
      ) : null}

      {!loading && !error && links.length === 0 ? (
        <Box className={classes.state}>
          <Typography variant="body2">
            Nenhuma conversa foi iniciada para este negocio.
          </Typography>
        </Box>
      ) : null}

      {!loading && !error && links.length > 0 ? (
        <Box className={classes.list}>
          {links.map((link) => {
            const ticket = link.ticket || {};
            return (
              <Box key={link.id} className={classes.item}>
                <Box className={classes.itemContent}>
                  <Box className={classes.ticketMeta}>
                    <Typography variant="subtitle2">
                      Conversa #{ticket.id}
                    </Typography>
                    <Chip size="small" label={ticket.channel || "canal"} />
                    <Chip size="small" label={ticket.status || "-"} />
                  </Box>
                  <Typography variant="body2" className={classes.message}>
                    {ticket.lastMessage || "Sem mensagem registrada"}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Vinculada em {formatDateTime(link.linkedAt)}
                    {link.linkedByUser?.name
                      ? ` por ${link.linkedByUser.name}`
                      : ""}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  startIcon={<OpenInNewIcon />}
                  onClick={() => history.push(`/tickets/${ticket.uuid}`)}
                  disabled={!ticket.uuid}
                >
                  Abrir
                </Button>
              </Box>
            );
          })}
        </Box>
      ) : null}
    </Box>
  );
};

export default DealTicketsPanel;
